import {
    useBlockProps,
    InspectorControls,
    BlockControls,
    BlockAlignmentToolbar,
} from "@wordpress/block-editor";
import {
    PanelBody,
    SelectControl,
    TextControl,
    TextareaControl,
    Button,
    ButtonGroup,
    Disabled,
    Spinner,
    Notice,
    __experimentalNumberControl as NumberControl,
    ToolbarGroup,
    ToolbarDropdownMenu,
} from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import useLinkedProfile from "../../../shared/js/useLinkedProfile";
import { __, sprintf } from "@wordpress/i18n";

const HEADING_TAG_OPTIONS = ["h1", "h2", "h3", "h4", "div"];

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"),  value: "default" },
    { label: __("Cards", "content-egg"),  value: "cards" },
    { label: __("Inline", "content-egg"),   value: "inline" },
];

const DATA_SOURCE_OPTIONS = [
    { value: "auto",   label: __("Auto-fill from linked profile", "content-egg") },
    { value: "manual", label: __("Manual override (custom items)", "content-egg") },
];

const addItem = (items, setAttributes) => {
    setAttributes({
        items: [...items, { quote: "", author: "", attribution: "", rating: 0 }],
    });
};

const updateItem = (items, index, key, value, setAttributes) => {
    setAttributes({ items: items.map((item, i) => i === index ? { ...item, [key]: value } : item) });
};

const removeItem = (items, index, setAttributes) => {
    setAttributes({ items: items.filter((_, i) => i !== index) });
};

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({
        className: attributes.align ? `align${attributes.align}` : "",
    });

    const {
        variant,
        heading_tag,
        color_scheme,
        data_source,
        items_limit,
        title,
        aggregate_rating,
        aggregate_rating_max,
        aggregate_count,
        aggregate_source,
        items,
    } = attributes;

    const { loading: profileLoading, summary: linkedProfile } = useLinkedProfile();

    const useSource = !!linkedProfile && data_source !== "manual";

    const hasManualData = items.length > 0 || aggregate_rating > 0;
    const showPreview = useSource || hasManualData;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
                <BlockAlignmentToolbar
                    value={attributes.align}
                    onChange={(val) => setAttributes({ align: val })}
                    controls={["wide", "full"]}
                />
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(val) => setAttributes({ variant: val })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={[
                            { label: __("Auto (site default)", "content-egg"), value: "auto" },
                            { label: __("Light", "content-egg"), value: "light" },
                            { label: __("Dark", "content-egg"), value: "dark" },
                        ]}
                        onChange={(val) => setAttributes({ color_scheme: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                {profileLoading && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={false}>
                        <Spinner />
                    </PanelBody>
                )}

                {!profileLoading && linkedProfile && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={true}>
                        <p style={{ fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Linked profile: %s", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </p>
                        <SelectControl
                            label={__("Testimonials & aggregate source", "content-egg")}
                            help={__(
                                "Auto-fill pulls testimonials and aggregate rating from the linked profile at render time. Switch to manual to enter custom values for this block only.",
                                "content-egg"
                            )}
                            value={data_source || "auto"}
                            options={DATA_SOURCE_OPTIONS}
                            onChange={(val) => setAttributes({ data_source: val })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(val) => setAttributes({ title: val })}
                        __nextHasNoMarginBottom
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {HEADING_TAG_OPTIONS.map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                </PanelBody>

                <PanelBody title={__("Aggregate rating", "content-egg")} initialOpen={false}>
                    {useSource ? (
                        <Notice status="info" isDismissible={false}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Aggregate rating will be hydrated from %s on save.", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </Notice>
                    ) : (
                        <>
                            <TextControl
                                label={__("Rating", "content-egg")}
                                help={__("e.g. 4.8 — leave 0 to hide aggregate.", "content-egg")}
                                type="number"
                                min={0}
                                max={5}
                                step={0.1}
                                value={aggregate_rating}
                                onChange={(val) => setAttributes({ aggregate_rating: parseFloat(val) || 0 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Max rating", "content-egg")}
                                type="number"
                                min={1}
                                value={aggregate_rating_max}
                                onChange={(val) => setAttributes({ aggregate_rating_max: parseFloat(val) || 5 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Review count", "content-egg")}
                                help={__("Leave 0 to omit the count.", "content-egg")}
                                type="number"
                                min={0}
                                value={aggregate_count}
                                onChange={(val) => setAttributes({ aggregate_count: parseInt(val) || 0 })}
                                __nextHasNoMarginBottom
                            />
                            <TextControl
                                label={__("Source", "content-egg")}
                                help={__("e.g. G2, Trustpilot", "content-egg")}
                                value={aggregate_source}
                                onChange={(val) => setAttributes({ aggregate_source: val })}
                                __nextHasNoMarginBottom
                            />
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__("Testimonials", "content-egg")} initialOpen={true}>
                    {useSource ? (
                        <>
                            <Notice status="info" isDismissible={false}>
                                {sprintf(
                                    /* translators: %s: linked profile name */
                                    __("Testimonials will be hydrated from %s on save.", "content-egg"),
                                    linkedProfile.profile_name
                                )}
                            </Notice>
                            <NumberControl
                                label={__("Max items to display", "content-egg")}
                                help={__("Limits how many testimonials are shown from the profile.", "content-egg")}
                                value={items_limit}
                                min={1}
                                max={12}
                                onChange={(val) => setAttributes({ items_limit: parseInt(val, 10) || 3 })}
                                __nextHasNoMarginBottom
                            />
                        </>
                    ) : (
                        <>
                            {items.map((item, i) => (
                                <div key={i} style={{ borderBottom: "1px solid #ddd", paddingBottom: "12px", marginBottom: "12px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                        <strong style={{ fontSize: "12px" }}>{item.author || __("Item", "content-egg") + " " + (i + 1)}</strong>
                                        <Button isDestructive size="small" icon="no-alt" onClick={() => removeItem(items, i, setAttributes)} label={__("Remove", "content-egg")} />
                                    </div>
                                    <TextareaControl
                                        label={__("Quote", "content-egg")}
                                        value={item.quote || ""}
                                        onChange={(v) => updateItem(items, i, "quote", v, setAttributes)}
                                        onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
                                        rows={3}
                                        __nextHasNoMarginBottom
                                    />
                                    <TextControl label={__("Author", "content-egg")} value={item.author || ""} onChange={(v) => updateItem(items, i, "author", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("Attribution", "content-egg")} help={__("e.g. Head of Product · Acme Corp", "content-egg")} value={item.attribution || ""} onChange={(v) => updateItem(items, i, "attribution", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl
                                        label={__("Rating (0 = hide)", "content-egg")}
                                        type="number"
                                        min={0}
                                        max={5}
                                        step={0.5}
                                        value={item.rating || 0}
                                        onChange={(v) => updateItem(items, i, "rating", parseFloat(v) || 0, setAttributes)}
                                        __nextHasNoMarginBottom
                                    />
                                </div>
                            ))}
                            <Button variant="secondary" size="small" onClick={() => addItem(items, setAttributes)}>
                                {__("Add testimonial", "content-egg")}
                            </Button>
                        </>
                    )}
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {showPreview ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/testimonial"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add testimonials in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
